// ---------------------------------------------------------------------------
// V11LayoutContext -- Layout Context Injection Pattern
// ---------------------------------------------------------------------------
// Allows child pages (e.g., FeltonExplorer) to inject components into the
// V11Layout header slot without modifying the layout directly.
//
// Usage:
//   1. V11Layout wraps its tree with <V11LayoutProvider>
//   2. V11Layout consumes `headerControls` to render the dynamic slot
//   3. Page components call `setHeaderControls(...)` to inject content
//   4. Page components call `setHeaderControls(null)` on unmount (cleanup)
// ---------------------------------------------------------------------------

import React, { createContext, useContext, useState, useCallback } from 'react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface V11LayoutContextValue {
  readonly headerControls: React.ReactNode | null
  readonly setHeaderControls: (controls: React.ReactNode | null) => void
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const V11LayoutContext = createContext<V11LayoutContextValue | undefined>(
  undefined
)

V11LayoutContext.displayName = 'V11LayoutContext'

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface V11LayoutProviderProps {
  readonly children: React.ReactNode
}

function V11LayoutProvider({ children }: V11LayoutProviderProps) {
  const [headerControls, setHeaderControlsState] = useState<React.ReactNode | null>(
    null
  )

  const setHeaderControls = useCallback(
    (controls: React.ReactNode | null) => {
      setHeaderControlsState(controls)
    },
    []
  )

  const value: V11LayoutContextValue = {
    headerControls,
    setHeaderControls,
  }

  return (
    <V11LayoutContext.Provider value={value}>
      {children}
    </V11LayoutContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

function useV11Layout(): V11LayoutContextValue {
  const context = useContext(V11LayoutContext)

  if (context === undefined) {
    throw new Error('useV11Layout must be used within V11LayoutProvider')
  }

  return context
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { V11LayoutContext, V11LayoutProvider, useV11Layout }
export type { V11LayoutContextValue }
